import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { ROUTES } from './paths';
import { AppShell } from '../components/layout/AppShell';
import { DriverLayout } from '../components/layout/DriverLayout';
import { LoginPage } from '../pages/LoginPage';
import { DashboardPage } from '../pages/DashboardPage';
import { DriverHomePage } from '../pages/DriverHomePage';
import { ForbiddenPage } from '../pages/ForbiddenPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { useAuth } from '../features/auth/useAuth';
import { homeRouteFor } from './homeRoute';
import { LoadingState } from '../components/ui/PageState';

/**
 * Las pantallas de catalogo y administracion se cargan bajo demanda.
 *
 * Son formularios y tablas que la mayoria de las sesiones no abre, y entrarian
 * en el paquete inicial haciendo mas lento el arranque para todos. El acceso,
 * el panel y la pantalla del chofer si van en el paquete inicial: son lo
 * primero que ve cada rol.
 */
const ProductsPage = lazy(async () => ({
  default: (await import('../pages/ProductsPage')).ProductsPage,
}));
const ProductDetailPage = lazy(async () => ({
  default: (await import('../pages/ProductDetailPage')).ProductDetailPage,
}));
const ProductAttributesPage = lazy(async () => ({
  default: (await import('../pages/ProductAttributesPage')).ProductAttributesPage,
}));
const AssetsPage = lazy(async () => ({
  default: (await import('../pages/AssetsPage')).AssetsPage,
}));
const InventoryEntriesPage = lazy(async () => ({
  default: (await import('../pages/InventoryEntriesPage')).InventoryEntriesPage,
}));
const InventoryEntryDetailPage = lazy(async () => ({
  default: (await import('../pages/InventoryEntryDetailPage')).InventoryEntryDetailPage,
}));
const StockPage = lazy(async () => ({
  default: (await import('../pages/StockPage')).StockPage,
}));
const LowStockPage = lazy(async () => ({
  default: (await import('../pages/LowStockPage')).LowStockPage,
}));
const ProjectReportPage = lazy(async () => ({
  default: (await import('../pages/ProjectReportPage')).ProjectReportPage,
}));
const KardexPage = lazy(async () => ({
  default: (await import('../pages/KardexPage')).KardexPage,
}));
const SurplusesPage = lazy(async () => ({
  default: (await import('../pages/SurplusesPage')).SurplusesPage,
}));
const OrdersPage = lazy(async () => ({
  default: (await import('../pages/OrdersPage')).OrdersPage,
}));
const OrderFormPage = lazy(async () => ({
  default: (await import('../pages/OrderFormPage')).OrderFormPage,
}));
const OrderDetailPage = lazy(async () => ({
  default: (await import('../pages/OrderDetailPage')).OrderDetailPage,
}));
const IncidentsPage = lazy(async () => ({
  default: (await import('../pages/IncidentsPage')).IncidentsPage,
}));
const EmailsPage = lazy(async () => ({
  default: (await import('../pages/EmailsPage')).EmailsPage,
}));
const DriverOrderPage = lazy(async () => ({
  default: (await import('../pages/DriverOrderPage')).DriverOrderPage,
}));
const ProjectsPage = lazy(async () => ({
  default: (await import('../pages/ProjectsPage')).ProjectsPage,
}));
const ContactsPage = lazy(async () => ({
  default: (await import('../pages/ContactsPage')).ContactsPage,
}));
const LocationsPage = lazy(async () => ({
  default: (await import('../pages/LocationsPage')).LocationsPage,
}));
const ProductCategoriesPage = lazy(async () => ({
  default: (await import('../pages/ProductCategoriesPage')).ProductCategoriesPage,
}));
const UnitsOfMeasurePage = lazy(async () => ({
  default: (await import('../pages/UnitsOfMeasurePage')).UnitsOfMeasurePage,
}));
const SuppliersPage = lazy(async () => ({
  default: (await import('../pages/SuppliersPage')).SuppliersPage,
}));
const UsersPage = lazy(async () => ({
  default: (await import('../pages/UsersPage')).UsersPage,
}));
const AuditPage = lazy(async () => ({
  default: (await import('../pages/AuditPage')).AuditPage,
}));

function HomeRedirect(): ReactNode {
  const { user, isBootstrapping } = useAuth();

  if (isBootstrapping) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <LoadingState label="Recuperando la sesión" />
      </div>
    );
  }

  return <Navigate to={user ? homeRouteFor(user.role) : ROUTES.login} replace />;
}

function Lazy({ children }: { children: ReactNode }): ReactNode {
  return <Suspense fallback={<LoadingState label="Cargando la seccion" />}>{children}</Suspense>;
}

/**
 * Las rutas se agrupan por sistema y cada grupo se monta en su propia
 * estructura: el ADMIN dentro del shell de sidebar y navbar, el DRIVER dentro
 * de su estructura mobile-first sin sidebar. Ver `docs/UI_STANDARDS.md`.
 *
 * Cada sistema cuelga de su propio prefijo —`/warehouse`, `/driver`— y de un
 * `Route` padre con el prefijo, de modo que anadir la siguiente aplicacion sea
 * agregar un bloque hermano y no repartir rutas sueltas por la raiz. Los
 * prefijos estan en `paths.ts` (ADR-022).
 *
 * Es una comodidad de navegacion, no una barrera: cada peticion que sale de
 * estas pantallas vuelve a pasar por `authenticate` y `authorize` en el
 * backend, que es donde se decide de verdad.
 */
export function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path={ROUTES.login} element={<LoginPage />} />
      <Route path="/" element={<HomeRedirect />} />

      <Route element={<ProtectedRoute />}>
        <Route path={ROUTES.forbidden} element={<ForbiddenPage />} />
      </Route>

      <Route element={<ProtectedRoute roles={['ADMIN']} />}>
        <Route element={<AppShell />}>
          <Route path={ROUTES.warehouse.root}>
            <Route index element={<Navigate to={ROUTES.warehouse.dashboard} replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route
              path="products"
              element={
                <Lazy>
                  <ProductsPage />
                </Lazy>
              }
            />
            <Route
              path="products/:id"
              element={
                <Lazy>
                  <ProductDetailPage />
                </Lazy>
              }
            />
            <Route
              path="product-attributes"
              element={
                <Lazy>
                  <ProductAttributesPage />
                </Lazy>
              }
            />
            <Route
              path="assets"
              element={
                <Lazy>
                  <AssetsPage />
                </Lazy>
              }
            />
            <Route
              path="inventory-entries"
              element={
                <Lazy>
                  <InventoryEntriesPage />
                </Lazy>
              }
            />
            <Route
              path="inventory-entries/:id"
              element={
                <Lazy>
                  <InventoryEntryDetailPage />
                </Lazy>
              }
            />
            <Route
              path="stock"
              element={
                <Lazy>
                  <StockPage />
                </Lazy>
              }
            />
            <Route
              path="low-stock"
              element={
                <Lazy>
                  <LowStockPage />
                </Lazy>
              }
            />
            <Route
              path="kardex"
              element={
                <Lazy>
                  <KardexPage />
                </Lazy>
              }
            />
            <Route
              path="surpluses"
              element={
                <Lazy>
                  <SurplusesPage />
                </Lazy>
              }
            />
            {/*
              `orders/new` va antes que `orders/:id` porque React Router
              escoge la ruta más específica, pero dejarlo escrito en este orden
              hace evidente por que "new" no se interpreta como identificador.
            */}
            <Route
              path="orders"
              element={
                <Lazy>
                  <OrdersPage />
                </Lazy>
              }
            />
            <Route
              path="orders/new"
              element={
                <Lazy>
                  <OrderFormPage />
                </Lazy>
              }
            />
            <Route
              path="orders/:id"
              element={
                <Lazy>
                  <OrderDetailPage />
                </Lazy>
              }
            />
            <Route
              path="orders/:id/edit"
              element={
                <Lazy>
                  <OrderFormPage />
                </Lazy>
              }
            />
            <Route
              path="incidents"
              element={
                <Lazy>
                  <IncidentsPage />
                </Lazy>
              }
            />
            <Route
              path="emails"
              element={
                <Lazy>
                  <EmailsPage />
                </Lazy>
              }
            />
            <Route
              path="project-report"
              element={
                <Lazy>
                  <ProjectReportPage />
                </Lazy>
              }
            />
            <Route
              path="projects"
              element={
                <Lazy>
                  <ProjectsPage />
                </Lazy>
              }
            />
            <Route
              path="contacts"
              element={
                <Lazy>
                  <ContactsPage />
                </Lazy>
              }
            />
            <Route
              path="locations"
              element={
                <Lazy>
                  <LocationsPage />
                </Lazy>
              }
            />
            <Route
              path="categories"
              element={
                <Lazy>
                  <ProductCategoriesPage />
                </Lazy>
              }
            />
            <Route
              path="units-of-measure"
              element={
                <Lazy>
                  <UnitsOfMeasurePage />
                </Lazy>
              }
            />
            <Route
              path="suppliers"
              element={
                <Lazy>
                  <SuppliersPage />
                </Lazy>
              }
            />
            <Route
              path="users"
              element={
                <Lazy>
                  <UsersPage />
                </Lazy>
              }
            />
            <Route
              path="audit-logs"
              element={
                <Lazy>
                  <AuditPage />
                </Lazy>
              }
            />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute roles={['DRIVER']} />}>
        <Route element={<DriverLayout />}>
          <Route path={ROUTES.driver.root}>
            <Route index element={<Navigate to={ROUTES.driver.orders} replace />} />
            <Route path="orders" element={<DriverHomePage />} />
            <Route
              path="orders/:id"
              element={
                <Lazy>
                  <DriverOrderPage />
                </Lazy>
              }
            />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
